import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Lock, Video, Sparkles, ArrowRight, Radio } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import Webinars from "@/pages/resources/Webinars";

/**
 * WebinarsPage — dedicated top-level route for the webinar hub.
 *
 * Free members are blocked with an upsell screen. Basic + Premium + Admin
 * see the full Webinars UI (live banner, next-webinar countdown, tag
 * filters, sorted library, bookmarks).
 */
export default function WebinarsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const tier = user?.tier || "free";
  const isFree = tier === "free" && user?.role !== "admin";

  if (isFree) {
    return (
      <div data-testid="webinars-locked" className="space-y-8">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-3">
            {t("webinarsPage.eyebrow")}
          </div>
          <h1 className="font-display font-bold text-4xl text-slate-900 tracking-tight">
            {t("webinarsPage.titleLocked")}
          </h1>
          <p className="mt-2 text-slate-600 max-w-2xl">
            {t("webinarsPage.subtitleLocked")}
          </p>
        </div>

        {/* Locked state */}
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white border border-amber-500/25 p-10 sm:p-12">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-amber-500/15 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-blue-500/15 blur-3xl" />
          <div className="relative max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 border border-amber-500/30 px-4 py-1.5 text-[10px] uppercase tracking-[0.24em] text-amber-300 font-bold mb-6">
              <Lock size={12} /> {t("webinarsPage.membersOnly")}
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tighter">
              {t("webinarsPage.includedTitle")}
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              {t("webinarsPage.includedDesc")}
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { Icon: Radio,    label: t("webinarsPage.features.liveTitle"),   sub: t("webinarsPage.features.liveSub")   },
                { Icon: Video,    label: t("webinarsPage.features.replayTitle"), sub: t("webinarsPage.features.replaySub") },
                { Icon: Sparkles, label: t("webinarsPage.features.notesTitle"),  sub: t("webinarsPage.features.notesSub")  },
              ].map((f) => (
                <div key={f.label} className="rounded-xl bg-white/5 border border-white/10 p-4">
                  <f.Icon size={16} className="text-amber-400 mb-2" />
                  <div className="text-sm font-semibold text-white">{f.label}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">{f.sub}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/pricing?src=webinars-locked">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-8">
                  {t("webinarsPage.unlockCta")} <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="h-12 px-8 border-white/20 text-white bg-transparent hover:bg-white/10 hover:border-white">
                  {t("webinarsPage.comparePlans")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Paid or admin — full Webinars component
  return (
    <div data-testid="webinars-page" className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-3">
          {t("webinarsPage.eyebrow")}
        </div>
        <h1 className="font-display font-bold text-4xl text-slate-900 tracking-tight">
          {t("webinarsPage.titleUnlocked")}
        </h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          {t("webinarsPage.subtitleUnlocked")}
        </p>
      </div>
      <Webinars />
    </div>
  );
}
